import { createSlice } from '@reduxjs/toolkit';

import { IControlQualityCheck, IWorkFlowFeedback } from 'common/api/type-def';
import { ViewType } from 'common/constants';

import {
	IAIModelDetails,
	IAIRecipeDetails,
	IAIRegFilterSearchWords,
} from 'features/AiSimulation/api/type-def';

import type { PayloadAction } from '@reduxjs/toolkit';

interface ISimulationState {
	simulationTabIndex: number;
	modelTabIndex: number;
	simulationControlTypeValue: string;
	simulationControlOwnerValue: string;
	simulationControlFrequencyValue: string;
	selectedAIModel: IAIModelDetails;
	selectedAIRecipe: IAIRecipeDetails;
	simulationViewTypeValue: ViewType;
	simulationInstitutionValue: string;
	regulationTableSearchText: string;
	advFilterDrawerState: boolean;
	regulationFilters: any;
	appliedfilterKeys: any;
	regTableFilterSearchWords: IAIRegFilterSearchWords;
	regulationText: string;
	textFromFile: string;
	selectedRecipe: string;
	regulationControlSelectedTab: string;
	workflowFeedback: IWorkFlowFeedback[];
	controlQuality: IControlQualityCheck;
	resetCtrlDescription: boolean;
}

const initialState: ISimulationState = {
	simulationTabIndex: 0,
	modelTabIndex: 0,
	selectedAIModel: {} as IAIModelDetails,
	selectedAIRecipe: {} as IAIRecipeDetails,
	simulationControlTypeValue: 'Select an option',
	simulationControlOwnerValue: '',
	simulationControlFrequencyValue: 'Select an option',
	simulationViewTypeValue: ViewType?.GRID,
	simulationInstitutionValue: '',
	regulationTableSearchText: '',
	advFilterDrawerState: false,
	regulationFilters: [],
	appliedfilterKeys: [],
	regTableFilterSearchWords: {
		themeName: '',
		sectionName: '',
		jurisdiction: '',
		issuanceType: '',
	},
	regulationText: '',
	textFromFile: '',
	selectedRecipe: '',
	regulationControlSelectedTab: 'Search',
	workflowFeedback: [],
	controlQuality: {} as IControlQualityCheck,
	resetCtrlDescription: false,
};

export const simulationSlice = createSlice({
	name: 'simulation',
	initialState,
	reducers: {
		setModelTabIndex(state, action: PayloadAction<number>) {
			state.modelTabIndex = action.payload;
		},
		setSimulationTabIndex(state, action: PayloadAction<number>) {
			state.simulationTabIndex = action.payload;
		},
		setAIModelDetail: (state, action: PayloadAction<IAIModelDetails>) => {
			state.selectedAIModel = action.payload;
		},
		setAIRecipeDetail: (state, action: PayloadAction<IAIRecipeDetails>) => {
			state.selectedAIRecipe = action.payload;
		},
		setSimulationControlTypeValue(state, action: PayloadAction<string>) {
			state.simulationControlTypeValue = action.payload;
		},
		setSimulationControlOwnerValue(state, action: PayloadAction<string>) {
			state.simulationControlOwnerValue = action.payload;
		},
		setSimulationControlFrequencyValue(state, action: PayloadAction<string>) {
			state.simulationControlFrequencyValue = action.payload;
		},
		setSimulationViewTypeValue(state, action: PayloadAction<ViewType>) {
			state.simulationViewTypeValue = action.payload;
		},
		setSimulationInstitutionValue(state, action: PayloadAction<string>) {
			state.simulationInstitutionValue = action.payload;
		},
		setRegulationTableSearchText(state, action: PayloadAction<string>) {
			state.regulationTableSearchText = action.payload;
		},
		setAdvFilterDrawerState(state, action: PayloadAction<boolean>) {
			state.advFilterDrawerState = action.payload;
		},
		setRegulationFilters: (state, action: PayloadAction<any>) => {
			state.regulationFilters = action.payload;
		},
		setAppliedfilterKeys: (state, action: PayloadAction<any>) => {
			state.appliedfilterKeys = action.payload;
		},
		setRegTableFilterSearchWords: (
			state,
			action: PayloadAction<IAIRegFilterSearchWords>,
		) => {
			state.regTableFilterSearchWords = action.payload;
		},
		setRegulationText: (state, action: PayloadAction<string>) => {
			state.regulationText = action.payload;
		},
		setTextFromFile: (state, action: PayloadAction<string>) => {
			state.textFromFile = action.payload;
		},
		setSelectedRecipe: (state, action: PayloadAction<string>) => {
			state.selectedRecipe = action.payload;
		},
		setRegulationControlSelectedTab: (state, action: PayloadAction<string>) => {
			state.regulationControlSelectedTab = action.payload;
		},
		setWorkflowFeedback: (
			state,
			action: PayloadAction<IWorkFlowFeedback[]>,
		) => {
			state.workflowFeedback = action.payload;
		},
		addWorkflowFeedback: (state, action: PayloadAction<IWorkFlowFeedback>) => {
			const foundIndex = state.workflowFeedback.findIndex(
				(e) => e.workflowStep === action.payload.workflowStep,
			);
			const newWorkFlowFeedback = [...state.workflowFeedback];
			if (foundIndex !== -1) {
				newWorkFlowFeedback[foundIndex] = {
					...action.payload,
				};
			} else {
				newWorkFlowFeedback.push(action.payload);
			}
			state.workflowFeedback = [...newWorkFlowFeedback];
		},
		setControlQuality: (state, action: PayloadAction<IControlQualityCheck>) => {
			state.controlQuality = action.payload;
		},
		setResetCtrlDescription(state, action: PayloadAction<boolean>) {
			state.resetCtrlDescription = action.payload;
		},
	},
});

export const {
	setModelTabIndex,
	setSimulationTabIndex,
	setAIModelDetail,
	setAIRecipeDetail,
	setSimulationControlTypeValue,
	setSimulationControlOwnerValue,
	setSimulationControlFrequencyValue,
	setSimulationViewTypeValue,
	setSimulationInstitutionValue,
	setRegulationTableSearchText,
	setAdvFilterDrawerState,
	setRegulationFilters,
	setAppliedfilterKeys,
	setRegTableFilterSearchWords,
	setRegulationText,
	setTextFromFile,
	setSelectedRecipe,
	setRegulationControlSelectedTab,
	setWorkflowFeedback,
	addWorkflowFeedback,
	setControlQuality,
	setResetCtrlDescription,
} = simulationSlice.actions;

export default simulationSlice.reducer;
